import React from 'react';
import {
    Layout,
    TrendingUp,
    Shield,
    BarChart3,
    Zap,
    Settings,
    FileCode,
    Image as ImageIcon
} from 'lucide-react';
import { formatCurrency, formatPercentage } from '@/lib/utils';

interface AlgorithmPreviewProps {
    data: any;
    files: any[];
    images: any[];
}

export function AlgorithmPreview({ data, files, images }: AlgorithmPreviewProps) {
    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Main Header Preview */}
            <div className="bg-muted/10 border rounded-xl p-6">
                <div className="flex items-center gap-4 mb-4">
                    <h2 className="text-3xl font-bold">{data.name || 'Algorithm Name'}</h2>
                    {data.pricingType === 'free' && (
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">FREE</span>
                    )}
                </div>
                <p className="text-muted-foreground text-lg mb-6 line-clamp-3">
                    {data.description || 'Brief description will appear here...'}
                </p>

                <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                        <Layout className="h-4 w-4" />
                        <span className="capitalize">{data.category || 'Category'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                        <Settings className="h-4 w-4" />
                        <span>Platform: {data.requirements?.platform || 'TBD'}</span>
                    </div>
                </div>
            </div>

            {/* Performance Grid Preview */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-card border rounded-lg p-4">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                        <TrendingUp className="h-3 w-3 text-green-500" />
                        <span>Total Return</span>
                    </div>
                    <div className="text-xl font-bold text-green-500">
                        {formatPercentage(data.performanceMetrics?.totalReturn || 0)}
                    </div>
                </div>
                <div className="bg-card border rounded-lg p-4">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                        <Shield className="h-3 w-3 text-blue-500" />
                        <span>Sharpe Ratio</span>
                    </div>
                    <div className="text-xl font-bold text-blue-500">
                        {data.performanceMetrics?.sharpeRatio || '0.00'}
                    </div>
                </div>
                <div className="bg-card border rounded-lg p-4">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                        <BarChart3 className="h-3 w-3 text-purple-500" />
                        <span>Win Rate</span>
                    </div>
                    <div className="text-xl font-bold text-purple-500">
                        {formatPercentage(data.performanceMetrics?.winRate || 0)}
                    </div>
                </div>
                <div className="bg-card border rounded-lg p-4">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                        <Zap className="h-3 w-3 text-red-500" />
                        <span>Max Drawdown</span>
                    </div>
                    <div className="text-xl font-bold text-red-500">
                        {formatPercentage(data.performanceMetrics?.maxDrawdown || 0)}
                    </div>
                </div>
            </div>

            {/* Content Preview */}
            <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                        <FileCode className="h-5 w-5 text-primary" />
                        Uploaded Files ({files.length})
                    </h3>
                    <div className="space-y-2">
                        {files.map((f, i) => (
                            <div key={i} className="flex items-center gap-3 p-3 bg-muted/20 border rounded-lg text-sm">
                                <FileCode className="h-4 w-4 text-muted-foreground" />
                                <span>{f.name}</span>
                            </div>
                        ))}
                        {files.length === 0 && <p className="text-sm text-muted-foreground italic">No files uploaded yet</p>}
                    </div>
                </div>

                <div className="space-y-4">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                        <ImageIcon className="h-5 w-5 text-primary" />
                        Screenshots ({images.length})
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                        {images.map((img, i) => (
                            <div key={i} className="aspect-video bg-muted rounded-lg border flex items-center justify-center overflow-hidden">
                                {img.url ? <img src={img.url} alt={img.caption} className="w-full h-full object-cover" /> : <ImageIcon className="h-6 w-6 text-muted-foreground/20" />}
                            </div>
                        ))}
                        {images.length === 0 && Array.from({ length: 2 }).map((_, i) => (
                            <div key={i} className="aspect-video bg-muted/50 border border-dashed rounded-lg flex items-center justify-center">
                                <ImageIcon className="h-6 w-6 text-muted-foreground/10" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Pricing Section Preview */}
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 flex items-center justify-between">
                <div>
                    <div className="text-sm text-muted-foreground mb-1">Proposed Pricing</div>
                    <div className="text-3xl font-bold text-primary">
                        {data.pricingType === 'free' ? 'Free' : formatCurrency(data.price || data.subscriptionPrice?.monthly || 0)}
                        {data.pricingType === 'subscription' && <span className="text-lg font-normal">/mo</span>}
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-sm font-medium">Requirement</div>
                    <div className="text-muted-foreground font-mono text-sm">
                        Min. Capital: {formatCurrency(data.requirements?.minCapital || data.minCapital || 0)}
                    </div>
                </div>
            </div>
        </div>
    );
}
